import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { CustomerAuthProvider } from "./contexts/CustomerAuthContext";
import { useMotionMode } from "./contexts/MotionContext";
import { useSmoothScroll } from "./hooks/useSmoothScroll";
import { ScrollProgress } from "./components/motion/ScrollProgress";

const MARKETING_PATHS = ["/services", "/fleet", "/about", "/contact", "/privacy", "/terms"];
import ProtectedRoute from "./components/ProtectedRoute";
import Header from "./components/Header";
import Footer from "./components/Footer";
import CookieBanner from "./components/CookieBanner";
import Home from "./pages/Home";
import Services from "./pages/Services";
import ServiceDetail from "./pages/ServiceDetail";
import Fleet from "./pages/Fleet";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Booking from "./pages/Booking";
import BookingEdit from "./pages/BookingEdit";
import Login from "./pages/Login";
import CustomerLogin from "./pages/CustomerLogin";
import CustomerSignup from "./pages/CustomerSignup";
import MyTrips from "./pages/MyTrips";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import AdminOverview from "./pages/AdminOverview";
import AdminDashboard from "./pages/AdminDashboard";
import QuoteAcceptance from "./pages/QuoteAcceptance";
import DriverManagement from "./pages/DriverManagement";
import FleetManagement from "./pages/FleetManagement";
import DriverDetail from "./pages/DriverDetail";
import CalendarSettings from "./pages/CalendarSettings";
import BookingJobLog from "./pages/BookingJobLog";
import Customers from "./pages/Customers";
import CustomerDetail from "./pages/CustomerDetail";
import Dispatch from "./pages/Dispatch";
import LiveTracking from "./pages/LiveTracking";
import Invoices from "./pages/Invoices";
import DriverPortal from "./pages/DriverPortal";
import DriverLogin from "./pages/DriverLogin";
import DriverSignup from "./pages/DriverSignup";
import DriverJobs from "./pages/DriverJobs";
import InstallBanner from "./components/InstallBanner";
import ScrollToTop from "./components/ScrollToTop";
import WhatsAppButton from "./components/WhatsAppButton";
import PublicJob from "./pages/PublicJob";

const PublicChrome = ({ children }) => {
  const location = useLocation();
  const isChromeless =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/driver") ||
    location.pathname.startsWith("/job");
  const isDriverPath = location.pathname.startsWith("/driver");
  const isAdminPath = location.pathname.startsWith("/admin");
  const isMarketing = location.pathname === "/" || MARKETING_PATHS.some((p) => location.pathname.startsWith(p));
  const { reduced } = useMotionMode();
  useSmoothScroll(isMarketing && !reduced, 0.08);

  // Point the manifest + Apple web-app title at the right app-shell so the
  // admin console installs as "Obsidian Admin" (start_url /admin) and the
  // driver portal as "Obsidian Driver".
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    let href = "/manifest.webmanifest";
    let title = "Obsidian Driver";
    if (isAdminPath) { href = "/admin.webmanifest"; title = "Obsidian Admin"; }
    if (link) link.setAttribute("href", href);
    if (appleTitle) appleTitle.setAttribute("content", title);
  }, [isAdminPath]);

  return (
    <>
      {!isChromeless && <Header />}
      {isMarketing && <ScrollProgress />}
      {children}
      {!isChromeless && <Footer />}
      {!isChromeless && <CookieBanner />}
      {!isChromeless && <WhatsAppButton />}
      {isDriverPath && <InstallBanner appName="Obsidian Driver" />}
      {isAdminPath && <InstallBanner appName="Obsidian Admin" />}
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <CustomerAuthProvider>
            <PublicChrome>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/:slug" element={<ServiceDetail />} />
                <Route path="/fleet" element={<Fleet />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/booking" element={<Booking />} />
                <Route path="/booking/edit/:bookingId" element={<BookingEdit />} />
                <Route path="/login" element={<CustomerLogin />} />
                <Route path="/signup" element={<CustomerSignup />} />
                <Route path="/my-trips" element={<MyTrips />} />
                <Route path="/job/:token" element={<PublicJob />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/admin/login" element={<Login />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminOverview />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/bookings"
                  element={
                    <ProtectedRoute>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/bookings/:bookingId/log"
                  element={
                    <ProtectedRoute>
                      <BookingJobLog />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/drivers"
                  element={
                    <ProtectedRoute>
                      <DriverManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/drivers/:driverId"
                  element={
                    <ProtectedRoute>
                      <DriverDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/fleet"
                  element={
                    <ProtectedRoute>
                      <FleetManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/calendar"
                  element={
                    <ProtectedRoute>
                      <CalendarSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/customers"
                  element={
                    <ProtectedRoute>
                      <Customers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/customers/:customerId"
                  element={
                    <ProtectedRoute>
                      <CustomerDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/dispatch"
                  element={
                    <ProtectedRoute>
                      <Dispatch />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/tracking"
                  element={
                    <ProtectedRoute>
                      <LiveTracking />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/invoices"
                  element={
                    <ProtectedRoute>
                      <Invoices />
                    </ProtectedRoute>
                  }
                />
                <Route path="/quote/:bookingId" element={<QuoteAcceptance />} />
                <Route path="/driver/login" element={<DriverLogin />} />
                <Route path="/driver/signup" element={<DriverSignup />} />
                <Route path="/driver" element={<DriverJobs />} />
                <Route path="/driver/:bookingId" element={<DriverPortal />} />
              </Routes>
            </PublicChrome>
            <Toaster position="top-right" />
          </CustomerAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
